import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X, Play, Home, Camera, Sparkles, Video, ArrowUpRight, Compass } from "lucide-react";
import {
  isItemLive,
  resolveCtaHref,
  trackSectionEvent,
  youtubeEmbed,
  vimeoEmbed,
} from "@/lib/mediaHelpers";

/**
 * Iter55 — Interactive Experiences
 * ────────────────────────────────────────────────────────────────────────────
 * Premium showcase grid + click-to-open lightbox. Renders every experience
 * type via a single MediaViewer sub-component so future types (drone,
 * construction progress, VR, builder showcase) just need a new icon + label
 * — no rendering path change.
 *
 * Analytics uses the unified /api/analytics/section-event endpoint —
 * impression fires when an item's card scrolls into view (IntersectionObserver,
 * once per session), cta_click fires when the lightbox is opened or the
 * card CTA is clicked.
 */

// ── Type metadata (label + icon + badge colour) ───────────────────────────────
const TYPE_META = {
  walkthrough:       { label: "Walkthrough",         icon: Video,    tone: "accent"  },
  coohom:            { label: "360° Coohom Tour",    icon: Compass,  tone: "accent"  },
  matterport:        { label: "Matterport Tour",     icon: Compass,  tone: "accent"  },
  gallery_interior:  { label: "Interior Gallery",    icon: Home,     tone: "neutral" },
  gallery_exterior:  { label: "Exterior Gallery",    icon: Camera,   tone: "neutral" },
  ai_video:          { label: "AI Video",            icon: Sparkles, tone: "accent"  },
  external_embed:    { label: "External Experience", icon: Play,     tone: "neutral" },
  // Future-reserved. Rendered with a generic badge until they get their own path.
  drone:             { label: "Drone Flythrough",    icon: Video,    tone: "accent"  },
  construction:      { label: "Construction Progress", icon: Home,   tone: "neutral" },
  builder_showcase:  { label: "Builder Showcase",    icon: Home,     tone: "neutral" },
  interior_showcase: { label: "Interior Designer",   icon: Home,     tone: "neutral" },
  vr:                { label: "VR Experience",       icon: Compass,  tone: "accent"  },
  testimonial:       { label: "Client Story",        icon: Sparkles, tone: "accent"  },
};

const InteractiveExperiences = ({ section, items }) => {
  const live = useMemo(
    () => (items || []).filter((it) => isItemLive(it)).sort((a, b) => (a.order || 0) - (b.order || 0)),
    [items]
  );
  const [openId, setOpenId] = useState(null);
  const impressedRef = useRef(new Set());

  // Fire an impression on scroll-into-view — once per session per item.
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const id = e.target.getAttribute("data-item-id");
          if (!id || impressedRef.current.has(id)) return;
          impressedRef.current.add(id);
          trackSectionEvent("experiences", id, "impression");
        });
      },
      { threshold: 0.4 }
    );
    document.querySelectorAll("[data-experience-card]").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [live.length]);

  if (live.length === 0) return null;

  const openLightbox = (item) => {
    setOpenId(item.id);
    trackSectionEvent("experiences", item.id, "click");
  };

  const openItem = openId ? live.find((it) => it.id === openId) : null;

  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="experiences-section">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
        <div className="max-w-xl">
          <div className="flex items-center gap-4">
            <span className="hairline-accent w-10 shrink-0" />
            <span className="overline text-accent">{section?.eyebrow || "Interactive Experiences"}</span>
          </div>
          <h2 className="font-display text-5xl lg:text-6xl mt-5 tracking-tighter leading-[0.95]">
            {section?.heading_line1 || "See it,"}{" "}
            <span className="italic text-accent">{section?.heading_italic || "walk it, feel it —"}</span>{" "}
            {section?.heading_line3 || "before you break ground."}
          </h2>
          {section?.description && (
            <p className="mt-5 text-muted-foreground text-base lg:text-lg leading-relaxed">
              {section.description}
            </p>
          )}
        </div>
        {section?.footer_cta_label && section?.footer_cta_href && (
          <Link
            to={section.footer_cta_href}
            data-testid="experiences-footer-cta"
            className="overline text-accent hover:text-accent/70 transition-fast inline-flex items-center gap-2"
          >
            {section.footer_cta_label} <ArrowUpRight size={14} />
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {live.map((item, i) => (
          <ExperienceCard
            key={item.id || i}
            item={item}
            index={i}
            onOpen={() => openLightbox(item)}
          />
        ))}
      </div>

      {openItem && <Lightbox item={openItem} onClose={() => setOpenId(null)} />}
    </section>
  );
};

// ── Card ────────────────────────────────────────────────────────────────────
const ExperienceCard = ({ item, index, onOpen }) => {
  const meta = TYPE_META[item.experience_type] || TYPE_META.walkthrough;
  const Icon = meta.icon;
  const thumb =
    item.thumbnail_url ||
    item.poster_url ||
    // Fallback to a subtle placeholder tone
    "";
  return (
    <button
      type="button"
      onClick={onOpen}
      data-experience-card
      data-item-id={item.id}
      data-testid={`experience-card-${index}`}
      className="group relative aspect-[4/5] overflow-hidden bg-primary text-left lift-hover"
    >
      {thumb ? (
        <img src={thumb} alt={item.title || meta.label} className="plan-card-img w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-primary-foreground/40">
          <Icon size={48} strokeWidth={1} />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-primary/95 via-primary/40 to-transparent" />
      <div className="absolute inset-0 flex flex-col justify-between p-6 lg:p-7 text-primary-foreground">
        <div className="flex items-start justify-between">
          <div className="inline-flex items-center gap-1.5 bg-background/90 text-foreground px-2.5 py-1 text-[10px] uppercase tracking-[0.16em]" data-testid={`experience-badge-${index}`}>
            <Icon size={11} strokeWidth={1.75} className="text-accent" />
            <span>{meta.label}</span>
          </div>
          <div className="w-11 h-11 rounded-full border border-primary-foreground/40 bg-background/10 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-fast">
            <Play size={14} className="translate-x-[1px]" fill="currentColor" />
          </div>
        </div>
        <div>
          <div className="font-display text-2xl lg:text-3xl leading-[1.05] tracking-tight" data-testid={`experience-title-${index}`}>
            {item.title || meta.label}
          </div>
          {item.subtitle && (
            <div className="text-sm text-primary-foreground/75 mt-2 tracking-wide" data-testid={`experience-subtitle-${index}`}>
              {item.subtitle}
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

// ── Lightbox — renders any media type ────────────────────────────────────────
const Lightbox = ({ item, onClose }) => {
  // ESC to close
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    // Lock body scroll
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const meta = TYPE_META[item.experience_type] || TYPE_META.walkthrough;
  const cta = item.cta_label
    ? { type: item.cta_type || "internal", href: item.cta_href }
    : null;
  const ctaHref = cta ? resolveCtaHref(cta) : null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur flex items-center justify-center p-4 md:p-8"
      onClick={onClose}
      data-testid="experience-lightbox"
    >
      <div
        className="relative w-full max-w-6xl bg-background border border-border overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="experience-lightbox-close"
          aria-label="Close"
          className="absolute top-3 right-3 z-30 w-10 h-10 flex items-center justify-center bg-background/90 border border-border hover:bg-primary hover:text-primary-foreground transition-fast"
        >
          <X size={16} />
        </button>
        <div className="bg-black">
          <MediaViewer item={item} />
        </div>
        <div className="p-6 lg:p-8">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-accent">
            <meta.icon size={11} strokeWidth={1.75} />
            <span>{meta.label}</span>
          </div>
          <h3 className="font-display text-3xl lg:text-4xl mt-3 tracking-tight" data-testid="experience-lightbox-title">
            {item.title || meta.label}
          </h3>
          {item.subtitle && (
            <p className="text-muted-foreground mt-2 tracking-wide">{item.subtitle}</p>
          )}
          <div className="mt-6 flex flex-wrap gap-3">
            {ctaHref && (
              <Link
                to={ctaHref}
                onClick={() => trackSectionEvent("experiences", item.id, "cta_click")}
                data-testid="experience-lightbox-cta"
              >
                <Button className="lux-btn rounded-none bg-accent text-accent-foreground h-12 px-7">
                  {item.cta_label}
                </Button>
              </Link>
            )}
            {item.related_plan_id && (
              <Link
                to={`/plans/${item.related_plan_id}`}
                onClick={() => trackSectionEvent("experiences", item.id, "cta_click")}
                data-testid="experience-lightbox-plan"
              >
                <Button variant="outline" className="rounded-none h-12 px-7">
                  View the plan <ArrowUpRight size={14} className="ml-2" />
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Media renderer — pick correct element for the experience_type ────────────
const MediaViewer = ({ item }) => {
  const type = item.experience_type || "walkthrough";
  const primary = item.media_url || "";
  const provider = item.media_provider_url || "";

  // Uploaded MP4
  if (type === "walkthrough" || type === "ai_video" || type === "drone" || type === "construction") {
    if (provider && youtubeEmbed(provider)) return <IframeEmbed src={youtubeEmbed(provider)} />;
    if (provider && vimeoEmbed(provider)) return <IframeEmbed src={vimeoEmbed(provider)} />;
    if (primary) {
      return (
        <video
          src={primary}
          poster={item.poster_url || item.thumbnail_url || undefined}
          controls
          autoPlay
          playsInline
          className="w-full aspect-video object-cover"
        />
      );
    }
  }
  // 360° / Matterport / Coohom / external — always an iframe
  if (["coohom", "matterport", "external_embed", "vr"].includes(type)) {
    if (provider) return <IframeEmbed src={provider} />;
  }
  // Galleries — for MVP just show the first image; a later iteration can
  // upgrade this to a swipeable strip using <MediaCarousel />.
  if (type === "gallery_interior" || type === "gallery_exterior" || type === "builder_showcase" || type === "interior_showcase") {
    const src = primary || item.thumbnail_url || item.poster_url;
    if (src) return <img src={src} alt={item.title || ""} className="w-full aspect-video object-cover" />;
  }
  // Fallback — surface whatever thumb we have.
  const fallback = item.thumbnail_url || item.poster_url || primary;
  return (
    <div className="w-full aspect-video flex items-center justify-center bg-muted">
      {fallback ? (
        <img src={fallback} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="text-muted-foreground text-sm">No media available for this experience.</div>
      )}
    </div>
  );
};

const IframeEmbed = ({ src }) => (
  <iframe
    src={src}
    className="w-full aspect-video"
    style={{ border: 0 }}
    allow="autoplay; encrypted-media; picture-in-picture; xr-spatial-tracking; accelerometer; gyroscope"
    allowFullScreen
  />
);

export default InteractiveExperiences;
